import { useMemo, useState } from 'react';
import { AlertTriangle, Ban, Plus, Star } from 'lucide-react';
import { Badge } from '@/components/badge';
import { Button } from '@/components/button';
import { Input } from '@/components/input';
import { Skeleton } from '@/components/skeleton';
import { cn } from '@/lib/cn';
import type { ChosenRecipients } from '@/lib/mail-recipients';
import type { MailRecipientOptionData, MailRecipientsData } from '@/types/api';

type Slot = 'to' | 'cc' | null;

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Every address a client can be emailed at, each assignable to To or Cc.
 * Addresses that are not on any contact can be typed in and are rendered like
 * any other row.
 */
export function RecipientPicker({
  data,
  isLoading,
  value,
  onChange,
}: {
  data: MailRecipientsData | undefined;
  isLoading: boolean;
  value: ChosenRecipients;
  onChange: (value: ChosenRecipients) => void;
}) {
  const [draft, setDraft] = useState('');
  const [draftError, setDraftError] = useState<string | null>(null);

  const rows = useMemo<MailRecipientOptionData[]>(() => {
    const options = data?.options ?? [];
    const known = new Set(options.map((o) => key(o.address)));

    // Anything selected that no contact owns (typed in here, or remembered from
    // an earlier send) still needs a row so it can be moved or dropped.
    const strays = [...value.to, ...value.cc].filter((a) => !known.has(key(a)));

    return [...options, ...unique(strays).map(offListRow)];
  }, [data, value]);

  function slotOf(address: string): Slot {
    if (value.to.some((a) => key(a) === key(address))) return 'to';
    if (value.cc.some((a) => key(a) === key(address))) return 'cc';
    return null;
  }

  function setSlot(address: string, slot: Slot) {
    const without = {
      to: value.to.filter((a) => key(a) !== key(address)),
      cc: value.cc.filter((a) => key(a) !== key(address)),
    };

    if (slot === null) {
      onChange(without);
      return;
    }

    onChange({ ...without, [slot]: [...without[slot], address] });
  }

  function addDraft() {
    const address = draft.trim();
    if (!address) return;
    if (!EMAIL_PATTERN.test(address)) {
      setDraftError('That does not look like an email address.');
      return;
    }

    setSlot(address, slotOf(address) ?? 'to');
    setDraft('');
    setDraftError(null);
  }

  const blockedPicked = rows.some((r) => r.is_blocked && slotOf(r.address));

  if (isLoading && !data) {
    return (
      <div className="space-y-2">
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-12 w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {data?.remembered ? (
        <p className="text-xs text-muted-foreground">
          Preselected from the last time this was emailed.
        </p>
      ) : null}

      {rows.length === 0 ? (
        <p className="rounded-md border border-[var(--color-border)] bg-[var(--color-surface-muted)] p-3 text-sm text-muted-foreground">
          This client has no email addresses on file. Add one below, or add a contact first.
        </p>
      ) : (
        <ul className="divide-y divide-[var(--color-border)] rounded-md border border-[var(--color-border)]">
          {rows.map((row) => (
            <RecipientRow
              key={row.key}
              row={row}
              slot={slotOf(row.address)}
              onChange={(slot) => setSlot(row.address, slot)}
            />
          ))}
        </ul>
      )}

      <div>
        <div className="flex items-center gap-2">
          <Input
            type="email"
            placeholder="Add another address"
            value={draft}
            onChange={(e) => {
              setDraft(e.target.value);
              setDraftError(null);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                addDraft();
              }
            }}
          />
          <Button variant="secondary" onClick={addDraft} disabled={!draft.trim()}>
            <Plus className="size-4" /> Add
          </Button>
        </div>
        {draftError ? <p className="mt-1 text-xs text-[var(--color-danger)]">{draftError}</p> : null}
      </div>

      {blockedPicked ? (
        <p className="flex items-start gap-2 text-xs text-[var(--color-warning)]">
          <AlertTriangle className="mt-0.5 size-3.5 shrink-0" />
          One of the picked contacts is blocked. Send anyway only if you meant to.
        </p>
      ) : null}
    </div>
  );
}

function RecipientRow({
  row,
  slot,
  onChange,
}: {
  row: MailRecipientOptionData;
  slot: Slot;
  onChange: (slot: Slot) => void;
}) {
  return (
    <li className="flex items-center justify-between gap-3 px-3 py-2.5">
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="truncate text-sm font-medium">{row.name}</span>
          {row.is_primary_address && row.source === 'contact' ? (
            <Star className="size-3 fill-current text-[var(--color-warning)]" />
          ) : null}
          {row.roles.map((role) => (
            <Badge key={role} tone={role === 'billing' ? 'success' : 'neutral'}>
              {roleLabel(role)}
            </Badge>
          ))}
          {row.source === 'client' ? <Badge tone="info">Client record</Badge> : null}
          {row.source === 'off-list' ? <Badge tone="warning">Not on file</Badge> : null}
          {row.is_blocked ? (
            <Badge tone="danger">
              <Ban className="mr-1 size-3" /> Blocked
            </Badge>
          ) : null}
        </div>
        <p className="truncate text-xs text-muted-foreground">
          {row.address}
          {row.title ? ` · ${row.title}` : ''}
          {row.source === 'contact' ? ` · ${row.label}` : ''}
        </p>
      </div>

      <div className="flex shrink-0 overflow-hidden rounded-md border border-[var(--color-border)]">
        <SlotButton active={slot === 'to'} onClick={() => onChange(slot === 'to' ? null : 'to')}>
          To
        </SlotButton>
        <SlotButton active={slot === 'cc'} onClick={() => onChange(slot === 'cc' ? null : 'cc')}>
          Cc
        </SlotButton>
      </div>
    </li>
  );
}

function SlotButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        'w-11 py-1 text-xs font-medium transition-colors',
        active
          ? 'bg-[var(--color-primary)] text-[var(--color-primary-foreground)]'
          : 'text-muted-foreground hover:bg-[var(--color-surface-muted)]',
      )}
    >
      {children}
    </button>
  );
}

function roleLabel(role: string): string {
  if (role === 'billing') return 'Billing';
  if (role === 'approvals') return 'Approvals';
  if (role === 'technical') return 'Technical';
  return role;
}

function offListRow(address: string): MailRecipientOptionData {
  return {
    key: `off-list-${address}`,
    address,
    contact_id: null,
    name: address,
    title: null,
    label: 'typed in',
    roles: [],
    is_primary_contact: false,
    is_primary_address: false,
    is_blocked: false,
    source: 'off-list',
  };
}

function unique(addresses: string[]): string[] {
  const seen = new Set<string>();
  return addresses.filter((a) => {
    if (seen.has(key(a))) return false;
    seen.add(key(a));
    return true;
  });
}

function key(address: string): string {
  return address.trim().toLowerCase();
}
